import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Sparkles, 
  Calendar, 
  Clock, 
  RefreshCw, 
  ChevronRight,
  FileText,
  CheckCircle,
  MessageSquare,
  FolderKanban,
  Wallet,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const SummaryHistoryPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchSummaries();
  }, []);

  const fetchSummaries = async () => {
    try {
      setLoading(true);
      const res = await api.get('/summaries/', { params: { days: 30 } });
      setSummaries(res.data || []);
    } catch (error) {
      console.error('Error fetching summaries:', error);
      toast.error('Erreur lors du chargement des résumés');
    } finally {
      setLoading(false);
    }
  };

  const generateTodaySummary = async () => {
    try {
      setGenerating(true);
      const today = new Date().toISOString().split('T')[0];
      const res = await api.post('/summaries/generate', { date: today });
      
      if (res.data?.success) {
        toast.success('Résumé généré avec succès !');
        setSelectedSummary(res.data.summary);
        setShowModal(true);
        fetchSummaries(); // Refresh the list
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération du résumé');
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatShortDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const getActivityBadges = (activities) => {
    if (!activities) return null;
    
    const badges = [];
    if (activities.time_entries > 0) {
      badges.push({ icon: Clock, label: `${activities.time_entries} chronos`, color: 'bg-blue-100 text-blue-700' });
    }
    if (activities.completed_tasks > 0) {
      badges.push({ icon: CheckCircle, label: `${activities.completed_tasks} tâches`, color: 'bg-green-100 text-green-700' });
    }
    if (activities.active_tickets > 0) {
      badges.push({ icon: MessageSquare, label: `${activities.active_tickets} tickets`, color: 'bg-purple-100 text-purple-700' });
    }
    if (activities.new_projects > 0) {
      badges.push({ icon: FolderKanban, label: `${activities.new_projects} projets`, color: 'bg-orange-100 text-orange-700' });
    }
    if (activities.transactions > 0) {
      badges.push({ icon: Wallet, label: `${activities.transactions} transactions`, color: 'bg-emerald-100 text-emerald-700' });
    }
    
    return badges;
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Résumés IA
            </h1>
            <p className="text-sm text-[#64748b]">
              Historique des 30 derniers jours
            </p>
          </div>
        </div>
        
        <Button
          onClick={generateTodaySummary}
          disabled={generating}
          className="rounded-full gap-2"
          style={{ backgroundColor: theme.colors?.accent, color: theme.colors?.primary }}
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Génération en cours...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Générer le résumé du jour
            </>
          )}
        </Button>
      </div>

      {/* Summary List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
        </div>
      ) : summaries.length === 0 ? (
        <Card className="text-center py-16">
          <CardContent>
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-50" />
            <h3 className="text-lg font-semibold mb-2" style={{ color: theme.colors?.primary }}>
              Aucun résumé disponible
            </h3>
            <p className="text-[#64748b] mb-6">
              Générez votre premier résumé IA pour avoir un récapitulatif détaillé de vos activités.
            </p>
            <Button
              onClick={generateTodaySummary}
              disabled={generating}
              className="rounded-full gap-2"
              style={{ backgroundColor: theme.colors?.accent, color: theme.colors?.primary }}
            >
              <Sparkles className="w-4 h-4" />
              Générer le résumé du jour
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {summaries.map((summary) => {
            const badges = getActivityBadges(summary.activities_count);
            const isToday = summary.date === new Date().toISOString().split('T')[0];
            
            return (
              <Card 
                key={summary.id || summary.date}
                className={cn(
                  "cursor-pointer hover:shadow-md transition-all",
                  isToday && "ring-2 ring-offset-2"
                )}
                style={isToday ? { borderColor: theme.colors?.accent, '--tw-ring-color': theme.colors?.accent } : {}}
                onClick={() => {
                  setSelectedSummary(summary);
                  setShowModal(true);
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-[#64748b]" />
                        <span className="font-semibold capitalize" style={{ color: theme.colors?.primary }}>
                          {formatDate(summary.date)}
                        </span>
                        {isToday && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            Aujourd'hui
                          </Badge>
                        )}
                      </div>
                      
                      {/* Activity badges */}
                      {badges && badges.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {badges.map((badge, idx) => (
                            <Badge key={idx} className={cn("text-xs gap-1", badge.color)}>
                              <badge.icon className="w-3 h-3" />
                              {badge.label}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      {/* Preview of content */}
                      <p className="text-sm text-[#64748b] line-clamp-2">
                        {summary.content?.substring(0, 200)}...
                      </p>
                    </div>
                    
                    <ChevronRight className="w-5 h-5 text-[#64748b] flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Summary Detail Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
              <Sparkles className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Résumé du {selectedSummary && formatDate(selectedSummary.date)}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2">
            {selectedSummary && (
              <div className="space-y-4">
                {/* Activity summary badges */}
                {selectedSummary.activities_count && (
                  <div className="flex flex-wrap gap-2 pb-4 border-b">
                    {getActivityBadges(selectedSummary.activities_count)?.map((badge, idx) => (
                      <Badge key={idx} className={cn("text-xs gap-1", badge.color)}>
                        <badge.icon className="w-3 h-3" />
                        {badge.label}
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Markdown content */}
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => (
                        <h1 className="text-xl font-bold mt-4 mb-2" style={{ color: theme.colors?.primary }}>
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-lg font-semibold mt-4 mb-2" style={{ color: theme.colors?.primary }}>
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-base font-semibold mt-3 mb-1" style={{ color: theme.colors?.primary }}>
                          {children}
                        </h3>
                      ),
                      p: ({ children }) => (
                        <p className="text-[#475569] mb-3 leading-relaxed">
                          {children}
                        </p>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc list-inside mb-3 space-y-1">
                          {children}
                        </ul>
                      ),
                      li: ({ children }) => (
                        <li className="text-[#475569]">
                          {children}
                        </li>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold" style={{ color: theme.colors?.primary }}>
                          {children}
                        </strong>
                      ),
                    }}
                  >
                    {selectedSummary.content}
                  </ReactMarkdown>
                </div>
                
                {/* Generation info */}
                <div className="pt-4 border-t text-xs text-[#94a3b8]">
                  Généré le {new Date(selectedSummary.generated_at).toLocaleString('fr-FR')}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SummaryHistoryPage;
